import type { User } from '../types';

export const CONTRACTUAL_MANAGER_TAG = 'Contractual Manager';
export const CONTRACTUAL_MANAGER_TAGS = [CONTRACTUAL_MANAGER_TAG];

/** Display label for a contact: "First Last", falling back to email then id. */
export function getUserDisplayName(user: Pick<User, '_id' | 'firstname' | 'lastname' | 'email'>): string {
  const parts = [user.firstname, user.lastname].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : user.email || user._id;
}

export function hasContractualManagerTag(tags?: string[]): boolean {
  return tags?.some((tag) => tag.toLowerCase() === CONTRACTUAL_MANAGER_TAG.toLowerCase()) ?? false;
}

/** Minimal shape of a V4 account member returned by `getAccountMembersForPicker`. */
export interface ContractualManagerMember {
  mongoUserId: string;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
  tags?: string[];
}

/**
 * Build the Contractual Manager dropdown options by MERGING the V4 account members tagged
 * "Contractual Manager" with the V3 organisation contacts tagged the same — deduped by user
 * id and sorted by name. The currently-selected manager is always kept (even if untagged) so
 * an existing quote's saved value still displays.
 *
 * Bug it fixes: the previous logic short-circuited to V4 members only whenever the account had
 * any members, which dropped the V3 tagged contacts entirely — so a Contractual Manager that
 * exists only as a V3 contact never appeared in the dropdown (Hugo: "le contractual manager
 * n'apparaît pas").
 */
export function mergeContractualManagerOptions(
  v4Members: ReadonlyArray<ContractualManagerMember> | undefined,
  v3OrgUsers: ReadonlyArray<User> | undefined,
  selectedMongoUserId?: string,
): User[] {
  const v4Tagged: User[] = (v4Members ?? [])
    .filter((m) => hasContractualManagerTag(m.tags) || m.mongoUserId === selectedMongoUserId)
    .map((m) => ({
      _id: m.mongoUserId,
      firstname: m.firstname ?? undefined,
      lastname: m.lastname ?? undefined,
      email: m.email ?? undefined,
    }));

  const byId = new Map<string, User>();
  for (const user of [...(v3OrgUsers ?? []), ...v4Tagged]) {
    if (user?._id) byId.set(user._id, user);
  }

  return [...byId.values()].sort((a, b) =>
    `${a.firstname ?? ''} ${a.lastname ?? ''}`.trim().localeCompare(`${b.firstname ?? ''} ${b.lastname ?? ''}`.trim()),
  );
}
